// ABOUTME: The app shell rendered inside the router: public auth screens, or the me-gated app
// ABOUTME: Extracted from src/index.tsx so the entry file holds no component (Fast Refresh needs a module boundary)

import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"
import ExportReactFunctions from "@/exportReactFunctions"
import { LactateAutoImport } from "@/features/settings/components/FormSettings/ChooseGadgets/LactometerTE/LactateAutoImport"
import { LangLoad } from "@/locales"
import { UserProvider } from "@/model/user/UserProvider"
import AppRoutes from "@/pages/main/AppRoutes"
import { PublicAuthRoutes } from "@/pages/auth/PublicAuthRoutes"
import { AuthLoadingScreen } from "@/pages/auth/AuthLoading"
import { logoutUser } from "@/features/auth-support/fnLogout"
import { isOnAuthRoute } from "@/model/navigation"
import { logMetrikaPageView } from "@/model/metrika"
import { ChatWidget } from "@/features/chat"
import { ChatProvider } from "@/app/providers/ChatProvider"
import { RealtimeProvider } from "@/app/providers/RealtimeProvider"
import { CopyProvider } from "@/features/calendar/components/menu/CopyProvider"
import { OfflineIndicator } from "@shared/ui"
import { DeleteItemQuestion } from "@/model/discussion/DeleteItemQuestion"

const pageContext = {
  title: document.title,
  description: "Current page description",
  path: window.location.pathname,
  hash: window.location.hash,
}

// Public auth screens (/auth/*) live outside the app shell AND outside the me-gate.
// Branching on the path here — rather than nesting AppRoutes under a splat route —
// keeps AppRoutes' absolute paths intact and mounts UserProvider (which blocks on
// `me`) only for the authenticated app, so a logged-out user can reach /auth/*.
export const AppShell = () => {
  const { pathname } = useLocation()
  const { t } = useTranslation()

  // Tell the emergency-recovery watchdog in index.html that React is live.
  // Must be an effect (fires after the first commit): a module-level rAF can
  // fire while the pre-render async work is still pending, disarming the
  // white-screen watchdog before anything actually rendered.
  useEffect(() => {
    window.__markAppRendered?.()
  }, [])

  // Metrika counts the sign-in screens here, above the split: the app's routes never mount for them, and the counter
  // starts with `defer`, counting no page of its own (model/metrika). The app's own pages are counted by AppRoutes,
  // once the user has loaded and a redirect route has moved on: counted here, a direct /settings would be a view of
  // the loading screen and then one of /settings/profile. The address is read as it is now, not as rendered: a
  // sign-in route's <Navigate> runs its effect first, and /auth/callback's has already moved it into the app.
  useEffect(() => {
    if (isOnAuthRoute(window.location.pathname)) logMetrikaPageView()
  }, [pathname])

  // /logout runs an async session revocation (see the pre-app handler in src/index.tsx) then redirects.
  // Show the auth loader meanwhile instead of mounting the authenticated app (which would flash
  // the app / race a GetMe against the redirect) — the user is on their way back to the auth screens.
  if (pathname === "/logout") {
    return <AuthLoadingScreen note={t("auth.signingOut")} />
  }

  if (isOnAuthRoute(pathname)) {
    return <PublicAuthRoutes />
  }

  return (
    <UserProvider
      authLoadingFallback={<AuthLoadingScreen />}
      onUserGone={logoutUser}
    >
      <RealtimeProvider>
        <LangLoad />
        <OfflineIndicator />
        <ExportReactFunctions />
        <LactateAutoImport />
        <DeleteItemQuestion />
        <ChatProvider>
          <ChatWidget pageContext={pageContext} />
          <CopyProvider>
            <AppRoutes />
          </CopyProvider>
        </ChatProvider>
      </RealtimeProvider>
    </UserProvider>
  )
}

declare global {
  interface Window {
    __markAppRendered?: () => void
  }
}
